<template>
  <section v-if="provenance" class="recommendation-provenance" data-cy="report-ai-provenance">
    <h3>Design Recommendation — {{ scenario?.title }}</h3>
    <p>
      Selected {{ provenance.priority }} design · Applied {{ provenance.appliedAt }}
    </p>
    <p class="trace">Calculation reference: {{ provenance.traceId }}</p>
    <p v-if="equipmentChanged"><strong>Equipment has changed since the AI recommendation.</strong> The results below are recalculated from the saved engineering design.</p>
    <p v-if="provenance.verification.engineVersion" class="trace">
      Engine: {{ provenance.verification.engineVersion }}
    </p>
    <p>
      This records the selected recommendation. Report tables and curves reflect
      the saved sizing, including subsequent engineering edits.
    </p>
    <div v-if="provenance.completionRequirements?.length">
      <h4>Items requiring engineering completion</h4>
      <p>The original AI package was incomplete. Verify the saved design resolves these items before customer delivery.</p>
      <ul><li v-for="item in provenance.completionRequirements" :key="item.code">{{ item.equipmentType }}: {{ item.message }}</li></ul>
    </div>
    <div v-if="provenance.pros.length">
      <h4>Selection rationale</h4>
      <ul><li v-for="item in provenance.pros" :key="item">{{ item }}</li></ul>
    </div>
    <div v-if="provenance.cons.length || provenance.warnings.length">
      <h4>Recommendation considerations</h4>
      <ul>
        <li v-for="item in provenance.cons" :key="item">{{ item }}</li>
        <li v-for="item in warnings" :key="item.code">{{ item.label }}: {{ item.description }}</li>
      </ul>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { Scenario } from "@/composables/forms/formTypes";
import { readRecommendationProvenance, equipmentSelectionSignature } from "@/libs/ai/recommendationProvenance";
import { mapWarnings } from "@/libs/ai/warningLabels";
const props = defineProps<{ scenario?: Scenario | null }>();
const provenance = computed(() => readRecommendationProvenance(props.scenario?.ai_recommendation));
const equipmentChanged = computed(() => !!provenance.value?.appliedEquipmentSignature &&
  provenance.value.appliedEquipmentSignature !== equipmentSelectionSignature(props.scenario?.equipment));
const warnings = computed(() => mapWarnings(provenance.value?.warnings));
</script>

<style scoped>
.recommendation-provenance { break-inside: avoid; margin: 1.5rem 0; font-size: 0.85rem; }
.trace { overflow-wrap: anywhere; font-size: 0.75rem; }
</style>
